import * as React from "react";
import "../../billing/account/create/responsive.css";
import { useBlocker, useNavigate } from "react-router-dom";
import { ButtonProps, notification } from "antd";
import { request } from "../../../services/cpln";
import { getBillingErrorMessage } from "../../../services/utils";
import { slug } from "github-slugger";
import { Elements } from "@stripe/react-stripe-js";
import { Stripe } from "@stripe/stripe-js";
import { createOrgGVC } from "../../../services/onboard";
import { BasePathContext, useBasePath } from "../../../reactContexts/basePathContext";
import { CreateAccountLayout } from "../../billing/account/create/accountCreateLayout";
import { AccountCreateInfo } from "../../billing/account/create/info";
import { AccountCreateAddress } from "../../billing/account/create/address";
import { AccountCreatePaymentMethod } from "../../billing/account/create/addPaymentMethod";
import { AccountCreateOrgGvc } from "../../billing/account/create/stepOrgGvc";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { useInformHubspot } from "./useInformHubspot";
import { useInformExternal } from "./useInformExternal";
import { formSet, formSetValue, useCpFormAnyValue, useCpFormValue } from "../../gvc/testNewForm/utils";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { inputToBillingAccount } from "../utils";
import { store } from "../../../store";
import { AccountCreateFormStep, BillingAccountCreateInput } from "./input";
import { CpFormPrompt } from "../../../components/antd/form/CpFormPrompt";
import { CpFormValidator } from "../../gvc/testNewForm/CpFormValidator";
import { getValidatorCreate } from "../validator";
import { sentryLog } from "../../../tools/sentry";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { setOrg, setOrgAndGvc, setRedirect } from "../../../store/contextSlice";
import { fetchGvcs, fetchOrgs, selectHasAccount } from "../../../store/userDataSlice";

interface Props {
  stripePromise: Promise<Stripe | null>;
}
export const AccountCreateForm: React.FC<Props> = ({ stripePromise }) => {
  const dispatch = useAppDispatch();
  const hasAccount = useAppSelector(selectHasAccount);

  const { id } = useCpFormContext();
  const hubspotHook = useInformHubspot(id);
  const externalHook = useInformExternal(id);

  const validator = getValidatorCreate(id);

  const basePath = useBasePath("/account/-create/*", true, "home");
  const navigate = useNavigate();

  const ignoreBlockerRef = React.useRef(false);
  const nextLocationRef = React.useRef("/");
  const blocker = useBlocker(({ currentLocation, nextLocation, historyAction }) => {
    nextLocationRef.current = nextLocation.pathname;

    const res =
      !ignoreBlockerRef.current &&
      currentLocation.pathname.includes(`/home/account/-create`) &&
      !nextLocation.pathname.includes(`-create`) &&
      !nextLocation.pathname.includes(`/redirect`);

    if (res) {
      sentryLog("blockedNavigation", {
        "data.ignoreBlockerRef": ignoreBlockerRef.current,
        "pathname.next": nextLocation.pathname,
        "pathname.current": currentLocation.pathname,
        "pathname.check": "-create",
      });
    }

    return res;
  });

  React.useEffect(() => {
    return () => {
      console.log("unmounting w useBlocker", "src/pages/account/create/createForm.tsx");
    };
  }, []);

  const accountName = useCpFormValue(`account.accountName`);
  const fullName = useCpFormValue(`account.fullName`);
  const tou = useCpFormValue(`account.tou`);

  const orgName = useCpFormValue(`orgName`);
  const gvcName = useCpFormValue(`gvcName`);
  const locationNames = useCpFormValue(`locationNames`);

  const step = useCpFormValue(`meta.formStep`);
  const submitting = useCpFormAnyValue(`meta.submitting`);
  const formStep: AccountCreateFormStep = useCpFormValue(`meta.formStep`);
  const createdAccount = useCpFormValue(`meta.createdAccount`);

  const currentStepNumber = React.useMemo(() => {
    if (step === "info") {
      return 0;
    }
    if (step === "address") {
      return 1;
    }
    if (step === "paymentMethod") {
      return 2;
    }
    if (step === "orgGvc") {
      return 3;
    }
    return 0;
  }, [step]);

  const formButtons: ButtonProps[] = React.useMemo(() => {
    const formButtons: ButtonProps[] = [];
    if (formStep === "info") {
      formButtons.push({
        children: "Cancel",
        danger: true,
        type: "default",
        htmlType: "button",
        className: `flex-grow`,
        onClick: () => {
          ignoreBlockerRef.current = true;
          if (!hasAccount) {
            navigate("/home");
          } else {
            navigate(basePath.split("/-create")[0]);
          }
        },
        disabled: submitting,
      });
    } else if (formStep === "address") {
      formButtons.push({
        children: "Back (Account Info)",
        type: "default",
        htmlType: "button",
        className: `flex-grow`,
        icon: <ArrowLeftOutlined />,
        iconPosition: "start",
        onClick: () => {
          formSetValue(id, `meta.formStep`, "info");
        },
      });
    }

    if (formStep === "info") {
      formButtons.push({
        type: "primary",
        htmlType: "button",
        icon: <ArrowRightOutlined />,
        children: "Next (Address)",
        onClick: async () => {
          formSet(id, `meta.submitted`, true);

          const form = store.getState().form.instances[id];
          const values: BillingAccountCreateInput = form.values as any;

          const hasErrors = await validator(values);
          if (hasErrors) {
            return;
          }

          formSetValue(id, `meta.formStep`, "address");
        },
        className: "flex-grow",
      });
    } else if (formStep === "address") {
      formButtons.push({
        type: "primary",
        htmlType: "button",
        className: "action flex-grow",
        children: "Create & Continue",
        onClick: async () => {
          formSet(id, `meta.submitted`, true);

          const form = store.getState().form.instances[id];
          const values: BillingAccountCreateInput = form.values as any;

          const hasErrors = await validator(values);
          if (hasErrors) {
            return;
          }

          await onConfirmAccount();
        },
        disabled: submitting,
        loading: submitting,
      });
    } else if (formStep === "paymentMethod") {
      // Handled on payment component
    } else if (formStep === "orgGvc") {
      // assume org gvc
      formButtons.push({
        type: "primary",
        htmlType: "button",
        className: "action flex-grow",
        children: "Create & Finish",
        onClick: async () => {
          formSet(id, `meta.submitted`, true);

          const form = store.getState().form.instances[id];
          const values: BillingAccountCreateInput = form.values as any;

          const hasErrors = await validator(values);
          if (hasErrors) {
            return;
          }

          await onConfirmOrgGVC();
        },
        disabled: submitting,
        loading: submitting,
      });
    }

    return formButtons;
  }, [formStep, submitting]);

  async function onConfirmAccount() {
    if (!tou) {
      return;
    }
    try {
      hubspotHook.setIsDone(true);

      const form = store.getState().form.instances[id];
      const values: BillingAccountCreateInput = form.values as any;

      formSet(id, `meta.submitting`, true);
      const body = inputToBillingAccount(values);
      const { data: newAccount } = await request({
        method: "post",
        service: "billing-ng",
        url: `/account`,
        body: { account: body },
      });

      // api for accept tou
      try {
        await request({
          method: "post",
          service: "billing-ng",
          url: `/account/${newAccount.id}/tou`,
          body: {
            tou: {
              customerName: newAccount.fullName,
              accepted: tou,
            },
          },
        });
      } catch (e) {
        const errorMessage = getBillingErrorMessage(e);
        console.error("Failed to succeed on tou", errorMessage);
        notification.warning({
          message: "Failed to accept terms of service",
          description: errorMessage,
        });
      }

      const _accountId = newAccount.id;
      const _isDisabled = newAccount.enabled === false;

      formSetValue(id, `meta.createdAccount`, { disabled: _isDisabled, id: _accountId });

      // Inform HubSpot about account creation
      hubspotHook.informAccountCreation();

      // Inform external referral service
      // For disabled accounts: call inform() directly since navigate() below
      // may unmount the component before the useEffect triggered by setIsDone fires
      if (_isDisabled) {
        formSet(id, `meta.submitting`, false);
        notification.info({ message: "Redirecting, please wait." });
        ignoreBlockerRef.current = true;
        externalHook.inform();
        navigate(`/home/account/${_accountId}`);
        return;
      }

      externalHook.setIsDone(true);

      formSet(id, `meta.submitting`, false);
      formSetValue(id, `meta.formStep`, "paymentMethod");

      const nameToUse = accountName || fullName;
      const generatedOrgName = `${slug(nameToUse)}`;
      const generatedGvcName = `${slug(nameToUse)}`;
      formSetValue(id, `orgName`, generatedOrgName);
      formSetValue(id, `gvcName`, generatedGvcName);
    } catch (e) {
      notification.warning({
        message: "Failed",
        description: getBillingErrorMessage(e),
      });
      formSet(id, `meta.submitting`, false);
    }
  }

  async function onConfirmOrgGVC() {
    formSet(id, `meta.submitting`, true);
    const { status, message } = await createOrgGVC(createdAccount.id, orgName, gvcName, locationNames);
    switch (status) {
      case "none":
        formSet(id, `meta.submitting`, false);
        notification.warning({
          message: "Failed",
          description: message || "There is a failure. Please try again later.",
        });
        break;
      case "orgCreated":
        ignoreBlockerRef.current = true;
        notification.success({
          message: "Success",
          description: "Created Org, failed to create the GVC",
        });
        await dispatch(fetchOrgs()).unwrap();
        dispatch(setOrg(orgName));
        dispatch(setRedirect(`/console/org/${orgName}`));
        navigate("/redirect");
        break;
      case "gvcCreated":
        ignoreBlockerRef.current = true;
        notification.success({
          message: "Success",
          description: "Created Org and GVC",
        });
        await dispatch(fetchOrgs()).unwrap();
        await dispatch(fetchGvcs()).unwrap();
        dispatch(setOrgAndGvc({ org: orgName, gvc: gvcName }));
        dispatch(setRedirect(`/console/org/${orgName}/gvc/${gvcName}`));
        navigate("/redirect");
        break;
    }
  }

  return (
    <>
      <BasePathContext.Provider value={basePath}>
        <CpFormValidator validationFn={validator} />
        <CpFormPrompt blocker={blocker} nextLocationRef={nextLocationRef} />
        <CreateAccountLayout
          title={
            step === "info"
              ? "Create Account"
              : step === "address"
                ? "Address"
                : step === "paymentMethod"
                  ? "Add a Payment Method"
                  : "Create Org & GVC"
          }
          buttons={formButtons}
          stepCurrent={currentStepNumber}
        >
          {step === "info" ? <AccountCreateInfo /> : null}
          {step === "address" ? <AccountCreateAddress /> : null}
          {step === "paymentMethod" ? (
            <Elements stripe={stripePromise}>
              <AccountCreatePaymentMethod />
            </Elements>
          ) : null}
          {step === "orgGvc" ? <AccountCreateOrgGvc /> : null}
        </CreateAccountLayout>
      </BasePathContext.Provider>
    </>
  );
};
